import React from "react";
import clsx from "clsx";
import format from "date-fns/format";
import { useHistory, Link } from "react-router-dom";
import parseISO from "date-fns/parseISO";

import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Button from "@material-ui/core/Button";

import routes from "common/routes";
import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import SapLogo from "components/Icons/SapLogo";
import Horse from "components/Icons/Horse";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import MUIAccordionCustomExpandIcon from "components/MUIAccordionCustomExpandIcon";
import { useRacehorse360Api } from "hooks/api";
import { mapFormsToFacilitiesWithForms } from "utils/stall-applications";
import { pluralize } from "utils/text";
import useStyles from "./styles";

const RacingOfficialPage = () => {
  const classes = useStyles();
  const { currentUser } = useLoggedInUser();
  const history = useHistory();

  const { useListFacilities, useListStallApplicationForms } =
    useRacehorse360Api();

  const {
    data: { facilities } = { facilities: [] },
    isLoading: isListFacilitiesLoading
  } = useListFacilities(
    {
      query: {
        isActive: { value: true },
        isTrack: { value: true },
        ids: currentUser.facilityIdsListFromRoles
      },
      getOptions: {
        select: [
          "id",
          "name",
          "code",
          "backgroundColor",
          "foregroundColor",
          "strokeColor",
          "trainingFacilities:id",
          "trainingFacilities:name",
          "trainingFacilities:code"
        ],
        orderBy: ["name"]
      }
    },
    {
      onError: error => console.error(error)
    }
  );

  const {
    data: { stallApplicationForms } = { stallApplicationForms: [] },
    isLoading: isListStallApplicationLoading
  } = useListStallApplicationForms(
    "listStallApplicationForms",
    {
      query: {},
      getOptions: {
        select: [
          "id",
          "title",
          "start_date",
          "deadline_date",
          "close_date",
          "stallApplicationsCount",
          "facility_options:id",
          "facility_options:name",
          "facility_options:code",
          "facility_options:background_color",
          "facility_options:foreground_color",
          "facility_options:stroke_color",
          "facility_options:type",
          "facility_options:training_facilities"
        ]
      }
    },
    {
      onError: error => console.error(error)
    }
  );

  const handleAddNewFormClick = () => {
    history.push(routes.stallApplicationNewForm.path);
  };

  const getPathname = (formID: string) => {
    return routes.stallApplicationFormView.path.replace(
      ":stallApplicationFormId",
      formID
    );
  };

  const facilitiesWithForms = mapFormsToFacilitiesWithForms(
    facilities,
    stallApplicationForms
  );

  const renderFacilityLogo = (code: string) => {
    switch (code) {
      case "SA":
        return <SapLogo />;
    }
    return <Horse />;
  };

  const facilityKeys = Object.keys(facilitiesWithForms);

  return (
    <AppPage className={classes.root}>
      <AppPageContent
        className={clsx(classes.pageContent, {
          [classes.noContent]: !facilityKeys.length
        })}
      >
        {isListFacilitiesLoading || isListStallApplicationLoading ? (
          <Loader />
        ) : (
          <>
            {facilityKeys.length ? (
              facilityKeys.map(facilityId => {
                const facility = facilities.find(f => f.id === facilityId);
                return (
                  <Accordion
                    key={facilityId}
                    className={classes.facility}
                    defaultExpanded={false}
                  >
                    <AccordionSummary
                      expandIcon={<MUIAccordionCustomExpandIcon />}
                      classes={{
                        root: classes.facilityHeader,
                        content: classes.facilityHeaderContent
                      }}
                    >
                      <div
                        className={classes.facilityCircle}
                        style={{
                          backgroundColor: `#${facility.backgroundColor}`
                        }}
                      >
                        {renderFacilityLogo(facility.code)}
                      </div>
                      {facility.name.toLowerCase()}
                    </AccordionSummary>
                    <AccordionDetails className={classes.formsGroup}>
                      {facilitiesWithForms[facilityId].map(form => (
                        <div
                          key={form.id}
                          className={classes.stallApplicationForm}
                        >
                          <span>{form.title}</span>
                          <span>
                            Deadline:{" "}
                            {format(parseISO(form.deadlineDate), "MM/dd/yyyy")}
                          </span>
                          <span>
                            {form.stallApplicationsCount || "No"}{" "}
                            {pluralize(
                              Number(form.stallApplicationsCount),
                              "Applicant",
                              "s"
                            )}
                          </span>
                          <Link
                            to={{
                              pathname: getPathname(form.id),
                              state: { facilityId }
                            }}
                          >
                            <Button
                              variant={"contained"}
                              color={"primary"}
                              disableElevation
                              className={classes.stallApplicationFormViewButton}
                            >
                              View
                            </Button>
                          </Link>
                        </div>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <div className={classes.noFormsMessage}>No Forms Found</div>
            )}
            <Button
              className={classes.addNewFormButton}
              variant={"outlined"}
              onClick={handleAddNewFormClick}
            >
              CREATE NEW FORM
            </Button>
          </>
        )}
      </AppPageContent>
    </AppPage>
  );
};

export default RacingOfficialPage;
